// Global keyboard shortcuts.
//
// Two flavours of binding are exposed:
//
//   1. `useShortcut({ key, mod?, handler })` — single-keystroke
//      shortcuts. Modifier defaults to none; pass `mod: "mod"` to
//      bind Cmd on macOS / Ctrl on Windows. Suppressed while the
//      user is typing in an input, textarea, select, or contenteditable
//      surface.
//
//   2. `useChord()` — two-key sequences (the Gmail "g then i" style).
//      After pressing the first key of a chord, the second must be
//      pressed within `timeoutMs`; otherwise the prefix is forgotten
//      and the first key is treated as a normal keypress.
//
// The hook is intentionally low-level: it doesn't try to be a full
// "keyboard shortcut library" with declarative configuration. The
// shell wires Cmd+K to the command palette and the rail items get
// letter accelerators (g t, g c, g l, ...). Module-level shortcuts
// (e.g. the chat composer) are not registered here — they live
// inside the module so the right tree can listen.

import { useEffect, useRef } from "react";

const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
}

/** Returns the platform modifier — Cmd on Mac, Ctrl elsewhere. */
export function modKeyDisplay(): string {
  return isMac ? "⌘" : "Ctrl";
}

/** Single-keystroke shortcut. Ignores repeats. */
export function useShortcut({
  key,
  mod = false,
  shift = false,
  alt = false,
  enabled = true,
  allowInInputs = false,
  handler,
}: {
  /** Lowercase letter, digit, or named key ("escape", "enter", "/", "?"). */
  key: string;
  /** True to require the platform modifier (Cmd on Mac, Ctrl elsewhere). */
  mod?: boolean;
  shift?: boolean;
  alt?: boolean;
  enabled?: boolean;
  /** Set true for shortcuts that should fire even when the user is
   *  typing (e.g. the chat composer uses its own, not this one). */
  allowInInputs?: boolean;
  handler: (e: KeyboardEvent) => void;
}) {
  // Refs keep the handler stable across renders without re-binding
  // the listener every time. The listener reads the current handler
  // from the ref so the latest closure is always used.
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (!allowInInputs && isTypingTarget(e.target)) return;
      const wantsMod = mod;
      const gotMod = isMac ? e.metaKey : e.ctrlKey;
      if (wantsMod !== gotMod) return;
      if (shift !== e.shiftKey) return;
      if (alt !== e.altKey) return;
      // Normalise the key. Letters are compared case-insensitive;
      // named keys (Escape, Enter, etc.) are matched by lowercased
      // form against the supplied `key`.
      const pressed = e.key.length === 1 ? e.key.toLowerCase() : e.key.toLowerCase();
      if (pressed !== key.toLowerCase()) return;
      e.preventDefault();
      handlerRef.current(e);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [key, mod, shift, alt, enabled, allowInInputs]);
}

/** Two-key chord binding. After the prefix is pressed, the suffix
 *  must be pressed within `timeoutMs` (default 1200). Resets on
 *  any non-matching key. */
export function useChord<K extends string = string>(
  prefix: string,
  suffixes: Record<K, (e: KeyboardEvent) => void>,
  { enabled = true, timeoutMs = 1200, allowInInputs = false } = {},
) {
  const suffixesRef = useRef(suffixes);
  useEffect(() => {
    suffixesRef.current = suffixes;
  }, [suffixes]);

  useEffect(() => {
    if (!enabled) return;
    let armed: { key: string; at: number } | null = null;
    const onKey = (e: KeyboardEvent) => {
      if (!allowInInputs && isTypingTarget(e.target)) return;
      // Don't fire chords for modifier-prefixed keystrokes — those
      // belong to OS / single-shot shortcuts.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const pressed = e.key.length === 1 ? e.key.toLowerCase() : e.key.toLowerCase();
      if (armed) {
        if (Date.now() - armed.at > timeoutMs) armed = null;
      }
      if (armed && armed.key === prefix.toLowerCase()) {
        const suffix = (suffixesRef.current as Record<string, (e: KeyboardEvent) => void>)[pressed];
        if (suffix) {
          armed = null;
          return;
        }
        armed = null;
      }
      if (pressed === prefix.toLowerCase()) {
        armed = { key: pressed, at: Date.now() };
        // Slight visual indicator would be nice; for now we just
        // arm silently. The command palette can flash a hint.
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prefix, enabled, timeoutMs, allowInInputs]);
}
